import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Package, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { buildConfigComponents } from "./buildConfigComponents";
import type { ConfigComponent } from "./types";
import type {
  ModelComponentWithDefaults,
  ComponentTypeMaterialWithName,
  AttributeDefWithAttribute,
} from "@/types/supabase-helpers";

interface ModelImporterProps {
  onImport: (components: ConfigComponent[]) => void;
}

export function ModelImporter({ onImport }: ModelImporterProps) {
  const [open, setOpen] = useState(false);
  const [loadingModelId, setLoadingModelId] = useState<string | null>(null);

  // Fetch active non-custom product models
  const { data: models } = useQuery({
    queryKey: ["product-models-for-import"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_models")
        .select("id, name, description")
        .is("deleted_at", null)
        .eq("is_active", true)
        .eq("is_custom_template", false)
        .order("position");
      if (error) throw error;

      // Fetch component counts for each model
      if (!data || data.length === 0) return [];

      const modelIds = data.map((m) => m.id);
      const { data: counts } = await supabase
        .from("model_components")
        .select("product_model_id")
        .in("product_model_id", modelIds);

      const countMap = new Map<string, number>();
      counts?.forEach((c) => {
        countMap.set(c.product_model_id, (countMap.get(c.product_model_id) || 0) + 1);
      });

      return data.map((m) => ({
        ...m,
        componentCount: countMap.get(m.id) || 0,
      }));
    },
    enabled: open,
  });

  const handleSelectModel = async (modelId: string, modelName: string) => {
    setLoadingModelId(modelId);

    try {
      // Fetch model components (same query as OrderConfiguratorTab)
      const { data: components, error: componentsError } = await supabase
        .from("model_components")
        .select(`
          id,
          component_type_id,
          min_quantity,
          max_quantity,
          default_material_id,
          default_width,
          default_height,
          default_thickness,
          position,
          label,
          component_types (
            id,
            name,
            label,
            dimension_mode
          )
        `)
        .eq("product_model_id", modelId)
        .order("position");

      if (componentsError) throw componentsError;
      if (!components || components.length === 0) {
        toast.info(`Mallissa "${modelName}" ei ole komponentteja`);
        setLoadingModelId(null);
        setOpen(false);
        return;
      }

      const componentTypeIds = components.map((c) => c.component_type_id);

      // Fetch materials and attribute defs in parallel
      const [materialsResult, attributeDefsResult] = await Promise.all([
        supabase
          .from("component_type_materials")
          .select(`
            component_type_id,
            material_id,
            is_default,
            materials (id, name)
          `)
          .in("component_type_id", componentTypeIds)
          .order("position"),
        supabase
          .from("component_attribute_defs")
          .select(`
            id,
            component_type_id,
            attribute_id,
            is_required,
            default_value,
            position,
            attributes (id, name, label, attribute_type, options, is_required)
          `)
          .in("component_type_id", componentTypeIds)
          .order("position"),
      ]);

      const configComponents = buildConfigComponents(
        components as ModelComponentWithDefaults[],
        (materialsResult.data || []) as ComponentTypeMaterialWithName[],
        (attributeDefsResult.data || []) as AttributeDefWithAttribute[],
        { sourceModelName: modelName, forceMinQuantity1: true }
      );

      onImport(configComponents);
      toast.success(`Tuotu ${configComponents.length} komponenttia mallista "${modelName}"`);
    } catch (error) {
      console.error("Failed to import model:", error);
      toast.error("Mallin tuonti epäonnistui");
    } finally {
      setLoadingModelId(null);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full">
          <Package className="h-4 w-4 mr-2" />
          Tuo tuotemallista
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <p className="text-sm font-medium">Valitse tuotemalli</p>
          <p className="text-xs text-muted-foreground">Komponentit tuodaan oletusarvoilla</p>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {!models ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : models.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Ei tuotemalleja
            </p>
          ) : (
            models.map((model) => (
              <button
                key={model.id}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left text-sm hover:bg-muted transition-colors disabled:opacity-50"
                onClick={() => handleSelectModel(model.id, model.name)}
                disabled={loadingModelId !== null}
              >
                <span className="truncate">{model.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {loadingModelId === model.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      {model.componentCount} kpl
                    </Badge>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
